import Cookies from 'js-cookie';
import axios from 'axios';
import regenerateToken from '../components/regenerateToken';

/**
 * Returns the current view mode from sessionStorage.
 * 'admin' = working at admin level (no clientId injected)
 * 'client' = viewing a specific client (clientId injected if selected)
 */
const getViewMode = () => {
    try {
        return sessionStorage.getItem('adminViewMode') || 'client';
    } catch {
        return 'client';
    }
};

/**
 * Returns the resolved clientId for the current API call context.
 * - If admin in CLIENT mode with a selected client → returns that client's clientId
 * - In ADMIN mode or no client selected → returns null (API uses token to identify)
 */
export const getAdminSelectedClientId = () => {
    try {
        const saved = sessionStorage.getItem('adminSelectedClient');
        if (!saved) return null;
        const client = JSON.parse(saved);
        return client?.clientId || null;
    } catch {
        return null;
    }
};

/**
 * Builds standard request config, automatically injecting clientId for admin
 * ONLY when the admin is in 'client' mode and has a client selected.
 *
 * In 'admin' mode → no clientId injected (admin-level API call)
 * In 'client' mode with client selected → clientId injected automatically
 *
 * @param {Object} options - { method, url, data, extraHeaders, extraParams }
 */
export const buildRequestConfig = ({ method, url, data, extraHeaders = {}, extraParams = {} }) => {
    const idToken = Cookies.get('idToken');
    const viewMode = getViewMode();

    const params = { ...extraParams };

    // Inject clientId ONLY in client mode when a client is selected
    if (viewMode !== 'admin') {
        const adminClientId = getAdminSelectedClientId();
        if (adminClientId) {
            params.clientId = adminClientId;
        }
    }

    const config = {
        method,
        url,
        headers: {
            authorization: idToken,
            ...extraHeaders,
        },
        validateStatus: (status) => status >= 200 && status <= 401,
    };

    if (data !== undefined) {
        config.data = typeof data === 'string' ? data : JSON.stringify(data);
    }

    if (Object.keys(params).length > 0) {
        config.params = params;
    }

    return config;
};

/**
 * Safety-net wrapper around any axios call.
 * If the API returns 401 (token expired), it automatically:
 *   1. Calls regenerateToken() to get a fresh idToken
 *   2. Resets the loginTimeStamp so the session window stays alive
 *   3. Retries the original request once with the new token
 *
 * Usage: replace `await axios(config)` with `await requestWithRefresh(config)`
 *
 * @param {Object} config - The axios request config (from buildRequestConfig)
 */
export const requestWithRefresh = async (config) => {
    const response = await axios(config);

    if (response.status === 401) {
        console.warn('[Auth] 401 received — silently refreshing token and retrying...');
        try {
            await regenerateToken();
            localStorage.setItem('loginTimeStamp', new Date().getTime());
            const freshToken = Cookies.get('idToken');

            const retryConfig = {
                ...config,
                headers: {
                    ...config.headers,
                    authorization: freshToken,
                },
            };
            return await axios(retryConfig);
        } catch (e) {
            console.error('[Auth] Token refresh failed during retry:', e.message);
        }
    }

    return response;
};
